/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback } from 'react';

import useFluxibleAction from 'js/lib/fluxibleHooks/useFluxibleAction';

import { Typography2 } from '@coursera/cds-core';

import { TrackChooser } from 'bundles/interactive-transcript';
import type Track from 'bundles/interactive-transcript/models/Track';
import type TrackList from 'bundles/interactive-transcript/models/TrackList';
import { getVideoTranscript } from 'bundles/video-logged-out-page/actions/getVideoTranscript';
import PlainTextTranscript from 'bundles/video-logged-out-page/components/PlainTextTranscript';
import { useVideoTrimmingContext } from 'bundles/video-trim';

import _t from 'i18n!nls/video-logged-out-page';

const styles = {
  videoTranscript: css`
    display: block;
    margin-top: 25px;

    &.is-hidden {
      display: none;
    }
  `,

  vlpContainerSection: css`
    margin-bottom: 2.5rem;
  `,
  vlpTranscriptHeader: css`
    background-color: #eaeaea;
    min-height: 35px;
    padding: 0 15px;
    justify-content: space-between;
    align-items: center;
    display: flex;
    flex-direction: row;

    @media (max-width: 991px) {
      background-color: #f7f7f7;
    }
  `,
  trackChooser: css`
    padding: 0.5rem;

    @media (max-width: 767px) {
      width: 150px;
    }

    label[for='select-language'] {
      margin: 0;
    }
  `,
  vlpTranscriptHeaderTitle: css`
    margin: 0;
    font-weight: 700;
  `,
  vlpTranscriptContent: css`
    background-color: #f7f7f7;
    padding: 10px 15px;
    max-height: 375px;
    overflow-y: auto;

    @media (max-width: 991px) {
      max-height: 235px;
      padding-top: 5px;
      padding-bottom: 5px;
    }
  `,
};

type Props = {
  defaultTrack?: Track;
  tracks: TrackList;
  track: Track;
  videoPlayer?: any;
};

const VideoTranscript = ({ defaultTrack, track, tracks }: Props) => {
  const executeAction = useFluxibleAction();
  const { trimmedStartTimeSeconds, trimmedEndTimeSeconds } = useVideoTrimmingContext();
  const handleLanguageChange = useCallback(
    (trackInNewLanguage: Track) => {
      if (trackInNewLanguage) {
        executeAction(getVideoTranscript, {
          track: trackInNewLanguage,
          trimRange: trimmedEndTimeSeconds ? { trimmedStartTimeSeconds, trimmedEndTimeSeconds } : undefined,
        });
      }
    },
    [executeAction, trimmedEndTimeSeconds, trimmedStartTimeSeconds]
  );

  const selectedTrack = track || defaultTrack;

  return (
    <aside css={[styles.videoTranscript, styles.vlpContainerSection]} data-e2e="video-transcript">
      <header css={styles.vlpTranscriptHeader}>
        <Typography2 component="h3" variant="subtitleMedium" css={styles.vlpTranscriptHeaderTitle}>
          {_t('Transcript')}
        </Typography2>
        {tracks && selectedTrack && (
          <TrackChooser tracks={tracks} selectedTrack={selectedTrack} onTrackSelect={handleLanguageChange} />
        )}
      </header>
      <main css={styles.vlpTranscriptContent}>
        <PlainTextTranscript track={track} />
      </main>
    </aside>
  );
};

export default VideoTranscript;
